import { useCountryByLocCode } from "api/features/country/queries";
import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";
import { EpidemiologyDataResponse } from "@/data-types/api/epidemiologyData.types";
import { INITIAL_LOC_CODE } from "config/config";
import { getInitialValues } from "../../layouts/dashboards/analytics/schemas/countrySchema/initialValues";
import { CountryDataResponse } from "@/data-types/api/countryData.types";
import { useEpidemiologyByLocCodeAndOptions } from "api/features/epidemiology/queries";
import { getQueryErrorMessage } from "api/utils/queryError";

const emptyDefaults = getInitialValues(undefined as any, INITIAL_LOC_CODE);

const formAnalyticsContext = createContext<FormAnalyticsContextProps>({
  locCode: INITIAL_LOC_CODE,
  year: new Date().getFullYear(),
  isLoading: false,
  isFormLoading: false,
  isError: false,
  errorMessage: "",
  refetch: () => {},

  countryDefaults: emptyDefaults,
  liveFormValues: emptyDefaults,
  appliedOptions: null,

  countryData: null,
  epidemiologyData: null,

  refreshDataWithLocCode: () => {},
  setAppliedOptions: () => {},
});

interface FormAnalyticsContextProps {
  locCode: number;
  year: number;
  isLoading: boolean;
  isFormLoading: boolean;
  isError: boolean;
  errorMessage: string;
  refetch: () => void;

  /** Defaults from the selected country's API data (Reset buttons / CountryForm init). */
  countryDefaults: any;
  /** Values for export / year: appliedOptions ?? countryDefaults. */
  liveFormValues: any;
  /** Last submitted values — drives the epidemiology API query and charts. */
  appliedOptions: any | null;

  countryData: CountryDataResponse | null;
  epidemiologyData: EpidemiologyDataResponse | null;

  refreshDataWithLocCode: (locCode: number) => void;
  setAppliedOptions: (options: any | null) => void;
}

const FormAnalyticsContextProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [locCode, setLocCode] = useState<number>(INITIAL_LOC_CODE);
  const [appliedOptions, setAppliedOptions] = useState<any | null>(null);

  const refreshDataWithLocCode = useCallback((nextLocCode: number) => {
    setLocCode(Number(nextLocCode));
  }, []);

  const {
    data: countryData,
    isLoading: isCountryLoading,
    isError: isCountryError,
    error: countryError,
    refetch: refetchCountry,
  } = useCountryByLocCode(locCode);
  const {
    data: epidemiologyData,
    isLoading: isEpidemiologyLoading,
    isError: isEpidemiologyError,
    error: epidemiologyError,
    refetch: refetchEpidemiology,
  } = useEpidemiologyByLocCodeAndOptions(locCode, appliedOptions);

  const isLoading = isCountryLoading || isEpidemiologyLoading;
  const isError = isCountryError || isEpidemiologyError;
  const errorMessage = getQueryErrorMessage(epidemiologyError ?? countryError);

  const hasLoadedOnceRef = useRef(false);
  if (!isLoading) hasLoadedOnceRef.current = true;
  const isFormLoading = isLoading && !hasLoadedOnceRef.current;

  const refetch = useCallback(() => {
    void refetchCountry();
    void refetchEpidemiology();
  }, [refetchCountry, refetchEpidemiology]);

  const countryDefaults = useMemo(
    () => getInitialValues(countryData, locCode),
    [countryData, locCode]
  );

  const liveFormValues = useMemo(
    () => appliedOptions ?? countryDefaults,
    [appliedOptions, countryDefaults]
  );

  const year = liveFormValues?.year;

  const locCodeEffectAttempt = useRef(true);
  useEffect(() => {
    if (locCodeEffectAttempt.current) {
      locCodeEffectAttempt.current = false;
      return;
    }
    setAppliedOptions(null);
  }, [locCode]);

  // Drop legacy draft session key from the previous draftOptions system.
  useEffect(() => {
    sessionStorage.removeItem("currentOptionsAnalytics");
  }, []);

  const value = useMemo(
    () => ({
      locCode,
      year,
      isLoading,
      isFormLoading,
      isError,
      errorMessage,
      refetch,

      countryDefaults,
      liveFormValues,
      appliedOptions,

      countryData,
      epidemiologyData,

      refreshDataWithLocCode,
      setAppliedOptions,
    }),
    [
      locCode,
      year,
      isLoading,
      isFormLoading,
      isError,
      errorMessage,
      refetch,
      countryDefaults,
      liveFormValues,
      appliedOptions,
      countryData,
      epidemiologyData,
      refreshDataWithLocCode,
    ]
  );

  return <formAnalyticsContext.Provider value={value}>{children}</formAnalyticsContext.Provider>;
};

export const useFormAnalyticsContext = (): FormAnalyticsContextProps =>
  useContext(formAnalyticsContext);

export default FormAnalyticsContextProvider;
